import { applyCustomTheme, buildBrandRamp } from '@minthr-saas/ui-kit';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { buildCustomSkinCss } from 'utils/customSkinCss';

interface SkinContextProps {
  currentSkin: string;
  setSkin: (skin: string) => void;
  /** Accent hex of the active skin. See `resolveSkinPrimary`. */
  primaryColor: string;
  isCustomSkin: boolean;
}

const SkinContext = createContext<SkinContextProps | undefined>(undefined);

const DEFAULT_SKIN = 'mintrh';

const SKIN_PRIMARY: Record<string, string> = {
  mintrh: '#2c7955',
  '5': '#2D9596',
  '4': '#596FB7',
  '6': '#435585',
  '7': '#DF826C',
  '8': '#B15EFF',
  olga: '#195ABE',
  laura: '#f26c6d',
};

// The app spells Mint several ways depending on which entry point set it.
const SKIN_ALIASES: Record<string, string> = {
  minthr: DEFAULT_SKIN,
  mint: DEFAULT_SKIN,
};

const HEX_COLOR = /^#?[0-9a-f]{6}$/i;

/**
 * A skin value from anywhere — the cookie, the API, SignalR — reduced to its
 * canonical id: a preset name, or a bare lowercase hex for a client-picked
 * colour. Tolerates the raw cookie pair ('theme=olga'), which currentSkin can
 * briefly hold, a leading '#', and any casing.
 */
export const normalizeSkinId = (skin: string): string => {
  const key = (skin ?? '')
    .trim()
    .toLowerCase()
    .replace(/^theme=/, '');
  const id = SKIN_ALIASES[key] ?? key;

  if (SKIN_PRIMARY[id]) return id;
  if (HEX_COLOR.test(id)) return id.replace(/^#/, '');
  return DEFAULT_SKIN;
};

/** True when `skin` is a client-picked hex rather than one of the presets. */
export const isCustomSkin = (skin: string): boolean =>
  !SKIN_PRIMARY[normalizeSkinId(skin)];

export const toUsableAccent = (hex: string): string => {
  try {
    return buildBrandRamp(hex)[500];
  } catch {
    // Unparseable seeds never reach here via resolveSkinPrimary, but an accent
    // must never take the app down.
    return hex;
  }
};

export const resolveSkinPrimary = (skin: string): string => {
  const id = normalizeSkinId(skin);
  return SKIN_PRIMARY[id] ?? toUsableAccent(`#${id}`);
};

const applyKitTheme = (skin: string): void => {
  try {
    applyCustomTheme(resolveSkinPrimary(skin));
  } catch {
    // applyCustomTheme throws on an unparseable hex. Unreachable via
    // resolveSkinPrimary, but a bad accent must never take the app down.
  }
};

const appendSkinNode = (node: HTMLElement): void => {
  node.id = 'skin';
  document.head.appendChild(node);
};

const applySkinStylesheets = (skin: string): void => {
  document.querySelectorAll('#skin').forEach((node) => node.remove());

  const id = normalizeSkinId(skin);

  if (isCustomSkin(id)) {
    const style = document.createElement('style');
    style.textContent = buildCustomSkinCss(resolveSkinPrimary(id));
    appendSkinNode(style);
    return;
  }

  (['/skin', '/auth-skin'] as const).forEach((base) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `${base}/skin.${id}.css`;
    appendSkinNode(link);
  });
};

type SkinListener = (skin: string) => void;

/**
 * Mounted providers, notified by `UpdateSkin`.
 *
 * `UpdateSkin` is the escape hatch for applying a theme from outside React —
 * SignalR pushes a tenant theme change to every open tab, and ClientContext
 * applies the tenant's theme on boot. Both swap the skin stylesheets straight
 * on the DOM, which is why CSS-driven elements recolour instantly. Anything
 * React renders *from* the skin (`primaryColor`, the selected theme card) reads
 * `currentSkin`, so without this bridge it keeps the old value until a reload.
 */
const skinListeners = new Set<SkinListener>();

export const UpdateSkin = (skin: string) => {
  applySkinStylesheets(skin);
  applyKitTheme(skin);
  skinListeners.forEach((notify) => notify(normalizeSkinId(skin)));
};

export const SkinProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const themeCookie = document.cookie
    .split('; ')
    .find((row) => row.startsWith('theme='));
  const themeValue = themeCookie ? themeCookie.split('=')[1] : 'MintRH';
  const [currentSkin, setCurrentSkin] = useState<string>(themeValue);
  const [prevSkin, setPrevSkin] = useState<string | null>(null);
  // const { data } = useFetchData(GetThemeService, []);

  // Follow theme changes applied from outside React — see `skinListeners`.
  useEffect(() => {
    skinListeners.add(setCurrentSkin);
    return (): void => {
      skinListeners.delete(setCurrentSkin);
    };
  }, []);

  useEffect(() => {
    if (themeCookie) {
      setCurrentSkin(themeCookie);
    }

    if (themeValue && themeCookie !== themeValue) {
      setCurrentSkin(themeValue.toLowerCase());
      // localStorage.setItem('skin', themeValue);
    }
  }, []);

  useEffect(() => {
    if (prevSkin !== currentSkin) {
      applySkinStylesheets(currentSkin);
      document.cookie = `theme=${normalizeSkinId(currentSkin)}; path=/; max-age=31536000`;
      applyKitTheme(currentSkin);
      setPrevSkin(currentSkin);
    }
  }, [currentSkin, prevSkin]);

  const setSkin = (skin: string): void => {
    setCurrentSkin(skin);
  };

  return (
    <SkinContext.Provider
      value={{
        currentSkin,
        setSkin,
        primaryColor: resolveSkinPrimary(currentSkin),
        isCustomSkin: isCustomSkin(currentSkin),
      }}
    >
      {children}
    </SkinContext.Provider>
  );
};

export const useSkin = (): SkinContextProps => {
  const context = useContext(SkinContext);
  if (context === undefined) {
    throw new Error('useSkin must be used within a SkinProvider');
  }
  return context;
};
